import { decodeEntities } from '~/utils/stringHelper';

/**
 * Longest hero the directory intro shows without clipping: three lines at the
 * desktop measure hold about 190 characters.
 */
export const TEASER_HERO_MAX_CHARS = 190;

/** The two halves of a category teaser as the revamp page renders them. */
export interface TeaserSplit {
    /** Plain-text statement for the intro, at most `TEASER_HERO_MAX_CHARS`. */
    hero: string;
    /** The hero was cut mid-sentence to fit the cap. */
    heroTruncated: boolean;
    /**
     * Sanitised HTML for the "About" block at the foot of the page, empty when
     * the hero already carries the whole teaser.
     */
    fullTeaserHtml: string;
    /**
     * Sanitised HTML of the original copy a replacement hero would keep at the
     * foot: the archive once one exists, else the live text itself. A preview
     * renders this under the candidate, so the page is seen as it will ship.
     */
    originalHtml: string;
    /**
     * The text came from a `?previewField=` preview rather than the CMS (local
     * dev and the dev/team hosts only); nothing visible marks it, but the
     * intro carries a screen-reader status naming the preview.
     */
    previewed?: boolean;
}

/** Tokens a sentence-ending full stop belongs to rather than ends. */
const ABBREVIATIONS = new Set([
    'e.g',
    'i.e',
    'etc',
    'vs',
    'no',
    'mr',
    'mrs',
    'ms',
    'dr',
    'st',
    'approx',
    'incl',
    'inc',
    'ltd',
    'co',
    'u.s',
    'u.k',
    'n.z',
]);

/** Inline and block tags the foot block keeps; everything else is unwrapped. */
const KEPT_TAGS = new Set(['p', 'a', 'strong', 'em', 'br']);
/** Heading tags are demoted so CMS copy can never add a heading to the page. */
const HEADING_TAG = /^h[1-6]$/;
/**
 * Link targets the foot block keeps: web, mail, phone, relative and in-page. A
 * relative target's first segment may not hold `&`: the browser decodes
 * entities in an attribute before it reads the scheme, so
 * `javascript&colon;…` would otherwise pass as a relative path and run.
 */
const SAFE_HREF =
    /^(?:https?:|mailto:|tel:|\/|#|\.\/|\.\.\/|[^:/?#&]+(?:[/?#]|$))/i;

/**
 * Characters that render as nothing: the soft hyphen, zero-width spaces and
 * joiners, direction marks, overrides and isolates, invisible operators,
 * variation selectors, Hangul and Mongolian fillers, interlinear annotation
 * marks, the byte-order mark, and the tag and supplementary-selector block. A
 * search phrase with one inside is no longer that phrase to a search engine,
 * and hidden text on a page reads as manipulation. The same set as
 * `TeaserRow::INVISIBLE`.
 */
const INVISIBLE =
    // The class lists combining marks and variation selectors one by one to
    // strip each, never to match a grapheme.
    // oxlint-disable-next-line no-misleading-character-class
    /[\u00AD\u034F\u061C\u115F\u1160\u17B4\u17B5\u180B-\u180F\u200B-\u200F\u202A-\u202E\u2060-\u206F\u3164\uFE00-\uFE0F\uFEFF\uFFA0\uFFF9-\uFFFB\u{E0000}-\u{E0FFF}]/gu;

/**
 * Spaces that stand in for a plain one. The no-break space stays; editors use
 * it to keep "10 mm" on one line.
 */
const EXOTIC_SPACES = /[\u1680\u2000-\u200A\u202F\u205F\u3000]/gu;

/** CMS copy without the characters {@link INVISIBLE} names, its exotic spaces made plain. */
export const stripInvisible = (text: string): string =>
    text.replace(INVISIBLE, '').replace(EXOTIC_SPACES, ' ');

/**
 * Tags that end a block of text: a space keeps the words either side apart.
 * An inline tag (a link, bold) goes without one, so the full stop after a
 * link stays against its word.
 */
const BLOCK_TAG =
    /<\/?(?:p|div|br|hr|li|ul|ol|dl|dt|dd|h[1-6]|blockquote|pre|table|thead|tbody|tfoot|tr|td|th|section|article|header|footer|figure|figcaption)\b[^>]*>/gi;

/**
 * Named entities the CMS editor writes for typography and accented letters,
 * beyond the few `decodeEntities` knows. The console measures the teaser with
 * PHP's full HTML5 table, so each of these must count as one character here
 * too, or the two sides disagree on whether a teaser fits the hero.
 */
const NAMED_ENTITIES: Record<string, string> = {
    mdash: '—',
    ndash: '–',
    hellip: '…',
    lsquo: '‘',
    rsquo: '’',
    sbquo: '‚',
    ldquo: '“',
    rdquo: '”',
    bdquo: '„',
    laquo: '«',
    raquo: '»',
    apos: "'",
    deg: '°',
    copy: '©',
    reg: '®',
    trade: '™',
    times: '×',
    divide: '÷',
    plusmn: '±',
    frac12: '½',
    frac14: '¼',
    frac34: '¾',
    sup2: '²',
    sup3: '³',
    middot: '·',
    bull: '•',
    prime: '′',
    Prime: '″',
    euro: '€',
    pound: '£',
    cent: '¢',
    yen: '¥',
    sect: '§',
    para: '¶',
    micro: 'µ',
    ensp: ' ',
    emsp: ' ',
    thinsp: ' ',
    aacute: 'á',
    agrave: 'à',
    acirc: 'â',
    auml: 'ä',
    eacute: 'é',
    egrave: 'è',
    ecirc: 'ê',
    euml: 'ë',
    iacute: 'í',
    icirc: 'î',
    iuml: 'ï',
    oacute: 'ó',
    ocirc: 'ô',
    ouml: 'ö',
    uacute: 'ú',
    ucirc: 'û',
    uuml: 'ü',
    ccedil: 'ç',
    ntilde: 'ñ',
    Eacute: 'É',
};

/** `decodeEntities` plus {@link NAMED_ENTITIES}; an unknown name is left as written. */
const decodeTeaserEntities = (text: string): string =>
    decodeEntities(
        text.replace(
            /&([a-zA-Z][a-zA-Z0-9]*);/g,
            (match, name: string) => NAMED_ENTITIES[name] ?? match,
        ),
    );

/** The name of a tag {@link BLOCK_TAG} matches. */
const BLOCK_TAG_NAME =
    /^(?:p|div|br|hr|li|ul|ol|dl|dt|dd|h[1-6]|blockquote|pre|table|thead|tbody|tfoot|tr|td|th|section|article|header|footer|figure|figcaption)$/;

/** Plain text of a CMS HTML fragment with whitespace collapsed. */
export const teaserPlainText = (html: string): string =>
    stripInvisible(
        decodeTeaserEntities(
            html.replace(BLOCK_TAG, ' ').replace(/<[^>]*>/g, ''),
        ),
    )
        .replace(/\s+/g, ' ')
        .trim();

/**
 * Index just past the first sentence end in `text`, or -1 when there is none.
 * A sentence ends at `.`, `!` or `?` followed by whitespace and a capital,
 * digit or opening quote, unless the token before the stop is an abbreviation.
 */
const firstSentenceEnd = (text: string): number => {
    const boundary = /[.!?]+(?=\s+["'“‘(]?[A-Z0-9])/g;
    for (const match of text.matchAll(boundary)) {
        const before = text.slice(0, match.index);
        const token = before.split(/\s+/).pop()?.toLowerCase() ?? '';
        if (ABBREVIATIONS.has(token.replace(/\.$/, ''))) continue;
        return match.index + match[0].length;
    }
    return -1;
};

/**
 * Characters as the console counts them (PHP `mb_strlen`, code points), not
 * UTF-16 units: an emoji or other supplementary-plane character is one.
 */
const charCount = (text: string): number => Array.from(text).length;

/** Cut `text` at the last word boundary at or before `maxChars` characters. */
const cutAtWord = (text: string, maxChars: number): string => {
    const slice = Array.from(text).slice(0, maxChars).join('');
    const lastSpace = slice.lastIndexOf(' ');
    return (lastSpace > 0 ? slice.slice(0, lastSpace) : slice).replace(
        /[\s,;:–—-]+$/,
        '',
    );
};

/**
 * A fragment as a block of its own: one that does not open with a block tag
 * is wrapped in a paragraph, so two fragments set one after the other cannot
 * run their words together.
 */
const asBlock = (html: string): string =>
    /^\s*<(?:p|div|ul|ol|dl|h[1-6]|blockquote|pre|table|section|article|figure)\b/i.test(
        html,
    )
        ? html
        : `<p>${html}</p>`;

/**
 * Restricts teaser HTML to the tags the foot block renders: `p`, `a` (href
 * only, and only to a web, mail, phone, relative or in-page target), `strong`,
 * `em`, `br`. `span` and `div` are unwrapped, headings become bold paragraphs
 * (kept as `h2`/`h3` for bottom copy, whose parser reads them), and any other
 * tag is dropped with its attributes.
 */
export const sanitiseTeaserHtml = (
    html: string,
    keepHeadings = false,
): string =>
    stripInvisible(html)
        .replace(
            /<\/?([a-zA-Z0-9]+)([^>]*)>/g,
            (_match, rawTag: string, rawAttributes: string) => {
                const tag = rawTag.toLowerCase();
                const closing = _match.startsWith('</');
                if (HEADING_TAG.test(tag)) {
                    // The foot block demotes headings; the bottom copy keeps h2/h3,
                    // which is how its parser finds sections and questions.
                    if (keepHeadings && (tag === 'h2' || tag === 'h3')) {
                        return closing ? `</${tag}>` : `<${tag}>`;
                    }
                    return closing ? '</strong></p>' : '<p><strong>';
                }
                // A dropped block tag (a list item, a table cell) still ends
                // a run of words, so it leaves a space, as the plain text does.
                if (!KEPT_TAGS.has(tag))
                    return BLOCK_TAG_NAME.test(tag) ? ' ' : '';
                if (closing) return `</${tag}>`;
                if (tag === 'a') {
                    const href = rawAttributes.match(/href="([^"]*)"/i)?.[1];
                    return href !== undefined && SAFE_HREF.test(href.trim())
                        ? `<a href="${href}">`
                        : '<a>';
                }
                return tag === 'br' ? '<br>' : `<${tag}>`;
            },
        )
        // A tag the pattern never closed (`<img …` with no `>`) is still a tag
        // to the HTML parser, so it would run. Drop whatever is left unclosed.
        .replace(/<[^>]*$/g, '')
        .replace(/<p>\s*<\/p>/g, '')
        .trim();

/**
 * Splits a category teaser into the intro hero and the foot block.
 *
 * With an archived original (after the teaser migration) the whole of
 * `contentTopHtml` is the hero and the archive is the foot; should the live
 * text have outgrown the cap since, it is cut for the hero and rendered whole
 * ahead of the archive, so no copy leaves the page. With the live text empty
 * the archive is split as the live text would be. Without an archive the
 * hero is derived: the whole text when it fits, else the first sentence when
 * that fits, else a word-boundary cut flagged as truncated; the full teaser
 * then renders at the foot.
 */
export const splitTeaser = (
    contentTopHtml: string,
    archiveHtml = '',
    maxChars = TEASER_HERO_MAX_CHARS,
): TeaserSplit => {
    const plain = teaserPlainText(contentTopHtml);
    // An archive that reads the same as the live text is the original stored twice (a
    // restore beside an unchanged live text, say): showing both would put the one text
    // on the page twice, so the archive is set aside and the live text is split as if
    // there were none.
    if (archiveHtml && teaserPlainText(archiveHtml) === plain) {
        archiveHtml = '';
    }
    // With the live text emptied the archive is the page's only copy of its
    // own: the hero is derived from it as from a live text, rather than
    // leaving the intro blank.
    if (archiveHtml && plain === '') {
        return splitTeaser(archiveHtml, '', maxChars);
    }
    const originalHtml = sanitiseTeaserHtml(archiveHtml || contentTopHtml);
    if (archiveHtml) {
        const truncated = charCount(plain) > maxChars;
        return {
            hero: truncated ? cutAtWord(plain, maxChars) : plain,
            heroTruncated: truncated,
            fullTeaserHtml: sanitiseTeaserHtml(
                truncated
                    ? `${asBlock(contentTopHtml)}${asBlock(archiveHtml)}`
                    : archiveHtml,
            ),
            originalHtml,
        };
    }
    if (charCount(plain) <= maxChars) {
        return {
            hero: plain,
            heroTruncated: false,
            fullTeaserHtml: '',
            originalHtml,
        };
    }
    const fullTeaserHtml = originalHtml;
    const sentenceEnd = firstSentenceEnd(plain);
    if (sentenceEnd > 0 && charCount(plain.slice(0, sentenceEnd)) <= maxChars) {
        return {
            hero: plain.slice(0, sentenceEnd),
            heroTruncated: false,
            fullTeaserHtml,
            originalHtml,
        };
    }
    return {
        hero: cutAtWord(plain, maxChars),
        heroTruncated: true,
        fullTeaserHtml,
        originalHtml,
    };
};
